import { UciClient } from './uci-client.js';
import type { UciEngine } from './uci-engine.js';
import { parseBestMoves, parsePvLine, parseScoreLine, type BestMove } from './parse-uci.js';

export interface StrengthOpts {
  readonly skill?: number;
}

/**
 * High-level Stockfish interface. All engine quirks (MultiPV, UCI_Elo, timeouts)
 * live here; callers never touch UCI.
 */
export class ChessAI {
  private readonly uci: UciClient;
  private ready = false;

  constructor(engine: UciEngine) {
    this.uci = new UciClient(engine);
  }

  async init(): Promise<void> {
    await this.uci.ask('uci', (l) => l === 'uciok');
    await this.uci.ask('isready', (l) => l === 'readyok');
    this.ready = true;
  }

  get isReady(): boolean {
    return this.ready;
  }

  async setPosition(fen: string): Promise<void> {
    this.uci.post(`position fen ${fen}`);
  }

  async setStrength(elo: number, opts: StrengthOpts = {}): Promise<void> {
    this.uci.post('setoption name UCI_LimitStrength value true');
    this.uci.post(`setoption name UCI_Elo value ${elo}`);
    if (opts.skill !== undefined) {
      this.uci.post(`setoption name Skill Level value ${opts.skill}`);
    }
  }

  async getEval(movetimeMs = 300): Promise<number> {
    this.uci.post('setoption name MultiPV value 1');
    const lines = await this.askWithRetry(`go movetime ${movetimeMs}`, (l) => l.startsWith('bestmove'));
    const scored = lines.filter((l) => /\bscore (cp|mate)\b/.test(l)).at(-1);
    if (!scored) throw new Error('no eval from engine');
    return parseScoreLine(scored);
  }

  async getBestMoves(n: number, opts: { movetimeMs?: number; onInfo?: (line: string) => void; peek?: boolean } = {}): Promise<BestMove[]> {
    const ms = opts.movetimeMs ?? 500;
    this.uci.post(`setoption name MultiPV value ${n}`);
    // `peek: true` is a hint to test/deterministic engines that this is a
    // look-ahead call (game-store.predictOpponentReply) — they should
    // answer without consuming state. The real Stockfish ignores the
    // sentinel; it has no script to drain.
    const goLine = opts.peek ? `go movetime ${ms} peek` : `go movetime ${ms}`;
    const lines = await this.askWithRetry(goLine, (l) => l.startsWith('bestmove'), opts.onInfo);
    const moves = parseBestMoves(lines).slice(0, n);
    if (moves.length === 0) throw new Error('no best moves from engine');
    return moves;
  }

  async getBestLine(depth: number): Promise<string[]> {
    this.uci.post('setoption name MultiPV value 1');
    const lines = await this.askWithRetry(`go depth ${depth}`, (l) => l.startsWith('bestmove'));
    const pvLine = [...lines].reverse().find((l) => l.includes(' pv '));
    if (!pvLine) throw new Error('no line from engine');
    return parsePvLine(pvLine);
  }

  quit(): void {
    this.uci.post('quit');
  }

  /** Interrupts the current search without killing the engine (shared engine-safe). */
  stop(): void {
    this.uci.post('stop');
  }

  private async askWithRetry(line: string, match: (l: string) => boolean, onLine?: (l: string) => void): Promise<string[]> {
    try {
      return await this.uci.ask(line, match, 8000, onLine);
    } catch (err) {
      // Retry once per the spec (worker timeouts → best-known eval, retry once).
      return this.uci.ask(line, match, 8000, onLine);
    }
  }
}

export type { BestMove };
